import RouteEnum from '@/Enums/Routes/Route.enum';
import { useAppSelector } from '@/store/hooks';
import { selectIsSuperAdmin } from '@/store/selectors/me.selectors';
import {
  AddRounded,
  ContentCopyOutlined,
  DashboardCustomizeRounded,
  DeleteOutline,
  DriveFileRenameOutline,
  MoreVertRounded,
  ViewListRounded,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { type DashboardWidget, createDashboard, deleteDashboard, getDashboard, updateDashboard } from './api';
import DashboardBuilder from './components/DashboardBuilder';
import RenameDashboardDialog from './components/RenameDashboardDialog';
import { useCustomDashboards } from './hooks/useCustomDashboards';
import {
  awaitPlatformSignal,
  getCurrentPermissions,
  stripDeniedWidgetFields,
  useDashboardPermissions,
} from './lib/dashboardPermissions';
import { readTenantFields } from './lib/tenantFields';

// ─── Template definitions ─────────────────────────────────────────────────────

interface TemplateDefinition {
  id: string;
  name: string;
  description: string;
  category: 'Performance' | 'Delivery' | 'Engagement' | 'Executive';
  layout: DashboardWidget[];
}

export const TEMPLATES: TemplateDefinition[] = [
  {
    id: 'campaign-performance',
    name: 'Campaign Performance',
    description: 'Headline KPIs, a delivery trend and a campaign breakdown — the everyday review.',
    category: 'Performance',
    layout: [
      {
        id: 'w1',
        type: 'scorecard',
        title: 'Headline KPIs',
        metrics: ['impressions', 'clicks', 'spend', 'ctr'],
        showDelta: true,
        span: 3,
      },
      {
        id: 'w2',
        type: 'line',
        title: 'Delivery over time',
        metrics: ['impressions', 'clicks'],
        // Clicks run ~750x smaller than Impressions in a typical campaign, so
        // sharing the auto-split-by-format axis (both are 'count') flattens
        // Clicks to 0 — put it on its own axis by default (flank#5056); a
        // widget's own manual axis choice (#4408) still overrides this.
        axisOverrides: { clicks: 'y1' },
        timeGrain: 'day',
        span: 2,
      },
      {
        id: 'w3',
        type: 'donut',
        title: 'Mix by channel',
        metric: 'impressions',
        dimension: 'Channel',
        rowLimit: 8,
      },
      {
        id: 'w4',
        type: 'table',
        title: 'Top campaigns',
        dimension: 'Campaign',
        metrics: ['impressions', 'clicks', 'spend', 'ctr'],
        rowLimit: 10,
        span: 3,
      },
    ],
  },
  {
    id: 'budget-delivery',
    name: 'Budget & Pacing',
    description: 'Spend pacing against a target plus delivery trend — for mid-flight checks.',
    category: 'Delivery',
    layout: [
      { id: 'w1', type: 'pacing', title: 'Spend pacing', metric: 'spend', span: 2 },
      { id: 'w2', type: 'kpi', title: 'Impressions', metric: 'impressions', showDelta: true },
      {
        id: 'w3',
        type: 'area',
        title: 'Spend over time',
        metrics: ['spend'],
        timeGrain: 'week',
        span: 2,
      },
      { id: 'w4', type: 'goal', title: 'Impressions goal', metric: 'impressions' },
    ],
  },
  {
    id: 'engagement-tracker',
    name: 'Engagement & Funnel',
    description: 'Click and completion behaviour, with the impression → click → conversion funnel.',
    category: 'Engagement',
    layout: [
      {
        id: 'w1',
        type: 'scorecard',
        title: 'Engagement',
        metrics: ['clicks', 'ctr', 'vcr', 'conversions'],
        showDelta: true,
        span: 3,
      },
      {
        id: 'w2',
        type: 'funnel',
        title: 'Conversion funnel',
        metrics: ['impressions', 'clicks', 'conversions'],
        span: 2,
      },
      {
        id: 'w3',
        type: 'donut',
        title: 'Clicks by device',
        metric: 'clicks',
        dimension: 'Device',
        rowLimit: 8,
      },
      {
        id: 'w4',
        type: 'line',
        title: 'CTR trend',
        metrics: ['impressions', 'ctr'],
        timeGrain: 'day',
        span: 3,
      },
    ],
  },
  {
    id: 'channel-product-mix',
    name: 'Channel & Device Mix',
    description: 'How delivery splits across channel and device.',
    category: 'Delivery',
    layout: [
      {
        id: 'w1',
        type: 'donut',
        title: 'By channel',
        metric: 'impressions',
        dimension: 'Channel',
        rowLimit: 8,
      },
      {
        id: 'w2',
        type: 'donut',
        title: 'By device',
        metric: 'impressions',
        dimension: 'Device',
        rowLimit: 8,
      },
    ],
  },
  {
    id: 'geo-performance',
    name: 'Geographic Performance',
    description: 'Where delivery is landing — a state choropleth plus top Markets.',
    category: 'Performance',
    layout: [
      {
        id: 'w1',
        type: 'geo',
        title: 'Impressions by state',
        metric: 'impressions',
        geoLevel: 'state',
        span: 2,
      },
      {
        id: 'w2',
        type: 'scorecard',
        title: 'Totals',
        metrics: ['impressions', 'clicks'],
        showDelta: true,
      },
      {
        id: 'w3',
        type: 'geo',
        title: 'Top Markets by impressions',
        metric: 'impressions',
        geoLevel: 'dma',
        span: 3,
      },
    ],
  },
  {
    id: 'executive-summary',
    name: 'Executive Summary',
    description: 'One scorecard, one trend, one table — the whole account at a glance.',
    category: 'Executive',
    layout: [
      {
        id: 'w1',
        type: 'scorecard',
        title: 'Account totals',
        metrics: ['impressions', 'spend', 'clicks', 'conversions'],
        showDelta: true,
        span: 3,
      },
      {
        id: 'w2',
        type: 'line',
        title: 'Delivery trend',
        metrics: ['impressions', 'spend'],
        timeGrain: 'week',
        span: 3,
      },
      {
        id: 'w3',
        type: 'table',
        title: 'By advertiser',
        dimension: 'Advertiser',
        metrics: ['impressions', 'spend', 'clicks', 'ctr'],
        rowLimit: 10,
        span: 3,
      },
    ],
  },
];

const CATEGORY_COLORS = {
  Performance: 'primary',
  Delivery: 'success',
  Engagement: 'warning',
  Executive: 'secondary',
} as const;

// ─── Mini layout preview ──────────────────────────────────────────────────────

const WIDE_TYPES = new Set(['line', 'area', 'bar', 'geo']);

const TemplatePreview = ({ layout }: { layout: DashboardWidget[] }) => {
  type Cell = { wide: boolean };
  const rows: Cell[][] = [];
  let kpiBuf: boolean[] = [];

  const flush = () => {
    while (kpiBuf.length) {
      rows.push(kpiBuf.splice(0, 4).map(() => ({ wide: false })));
    }
  };

  for (const w of layout) {
    if (WIDE_TYPES.has(w.type as string)) {
      flush();
      rows.push([{ wide: true }]);
    } else {
      kpiBuf.push(false);
    }
  }
  flush();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, height: '100%' }}>
      {rows.map((row, ri) => (
        <Box key={ri} sx={{ display: 'flex', gap: 0.75, flex: row[0].wide ? 2 : 1 }}>
          {row.map((_cell, ci) => (
            <Box
              key={ci}
              sx={{
                flex: 1,
                borderRadius: 0.75,
                bgcolor: 'primary.main',
                opacity: row[0].wide ? 0.28 : 0.16,
              }}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const CustomDashboards = () => {
  const { dashboards, loading, error, refresh } = useCustomDashboards();
  // flank#5101: a template's widgets (e.g. Spend on Campaign Performance) are
  // static — applied through here with no permission check at all until now.
  // The hook call itself (not its return value — both call sites below read
  // fresh via getCurrentPermissions, after awaitPlatformSignal, to avoid
  // racing the probe on first paint) is what fires `ensurePlatformSignal` on
  // mount, so the probe is already in flight by the time either handler runs.
  const isRibeyeAdmin = useAppSelector(selectIsSuperAdmin);
  useDashboardPermissions(isRibeyeAdmin);
  // Which dashboard is open comes from the URL, not component state, so a
  // dashboard is bookmarkable/linkable and survives a refresh. A non-numeric or
  // junk :dashboardId falls back to the list rather than rendering a broken page.
  const navigate = useNavigate();
  const { dashboardId } = useParams<{ dashboardId?: string }>();
  const parsedId = Number(dashboardId);
  const selectedId = dashboardId && Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
  const openDashboard = (id: number) => navigate(`${RouteEnum.CUSTOM_DASHBOARDS}/${id}`);
  const closeDashboard = () => navigate(RouteEnum.CUSTOM_DASHBOARDS);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [clientScope, setClientScope] = useState('all');
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; id: number } | null>(null);
  const [creatingFromTemplate, setCreatingFromTemplate] = useState<string | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null);
  const [renaming, setRenaming] = useState<{ id: number; name: string } | null>(null);
  const [savingRename, setSavingRename] = useState(false);
  // Every mutation below used try/finally with no catch: a 500 or 422 left the
  // dialog sitting there, or the row silently not disappearing, with only an
  // unhandled rejection in the console. One transient error read as "create is
  // broken".
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ id: number; name: string } | null>(null);

  if (selectedId !== null) {
    return (
      <DashboardBuilder
        dashboardId={selectedId}
        onBack={() => {
          closeDashboard();
          refresh();
        }}
        onSaved={refresh}
      />
    );
  }

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setCreating(true);
    setActionError(null);
    try {
      const created = await createDashboard(trimmed);
      setDialogOpen(false);
      setNewName('');
      setClientScope('all');
      openDashboard(created.id);
    } catch {
      setActionError("Couldn't create that dashboard. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  const handleOpenDialog = () => {
    setNewName('');
    setClientScope('all');
    setActionError(null);
    setDialogOpen(true);
  };

  const handleUseTemplate = async (template: TemplateDefinition) => {
    setCreatingFromTemplate(template.id);
    setActionError(null);
    try {
      // flank#5101 AC5: a user without Spend who uses a template gets no
      // Spend widgets, not a dashboard full of cards they can't see. Awaits
      // the platform/spend/attribution probe first (CodeRabbit) — `permissions`
      // can still be at its optimistic pre-response default on first paint,
      // and reading it straight from the closure here would strip against
      // that stale snapshot even after the probe resolves moments later.
      await awaitPlatformSignal(isRibeyeAdmin);
      const { layout } = stripDeniedWidgetFields(template.layout, getCurrentPermissions(isRibeyeAdmin));
      const created = await createDashboard(template.name, undefined, layout);
      openDashboard(created.id);
    } catch {
      setActionError(`Couldn't create a dashboard from "${template.name}". Please try again.`);
    } finally {
      setCreatingFromTemplate(null);
    }
  };

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant='h4' sx={{ fontWeight: 700 }}>
            Dashboards
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Build your own dashboards for any use case.
          </Typography>
          {/*
            Page-level, not dialog-level: "Use template" is triggered from the
            template grid with no dialog open, so a failure set actionError and
            rendered it nowhere — the button just reverted and nothing happened.
          */}
          {actionError && (
            <Typography variant='body2' color='error' sx={{ mt: 1 }}>
              {actionError}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant='outlined'
            startIcon={<ViewListRounded />}
            onClick={() => setTab(1)}
            sx={{ textTransform: 'none', borderColor: 'divider', color: 'text.primary' }}
          >
            Browse templates
          </Button>
          <Button
            variant='contained'
            disableElevation
            startIcon={<AddRounded />}
            onClick={handleOpenDialog}
            sx={{ textTransform: 'none' }}
          >
            Start blank
          </Button>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40 }}>
        <Tab label='My dashboards' sx={{ textTransform: 'none', minHeight: 40, fontWeight: 500 }} />
        <Tab label='Templates' sx={{ textTransform: 'none', minHeight: 40, fontWeight: 500 }} />
      </Tabs>

      {tab === 1 ? (
        <Box>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2.5 }}>
            Start from a pre-built layout and customise from there. A copy is saved to your dashboards.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(268px, 1fr))',
              gap: 2,
            }}
          >
            {TEMPLATES.map(template => (
              <Paper
                key={template.id}
                variant='outlined'
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ bgcolor: 'action.hover', p: 2, height: 100 }}>
                  <TemplatePreview layout={template.layout} />
                </Box>
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 0.75, flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Typography variant='body2' sx={{ fontWeight: 600, flexGrow: 1 }}>
                      {template.name}
                    </Typography>
                    <Chip
                      label={template.category}
                      size='small'
                      color={CATEGORY_COLORS[template.category]}
                      variant='outlined'
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  </Box>
                  <Typography variant='caption' color='text.secondary' sx={{ lineHeight: 1.4 }}>
                    {template.description}
                  </Typography>
                </Box>
                <Box sx={{ px: 2, pb: 2 }}>
                  <Button
                    fullWidth
                    variant='outlined'
                    size='small'
                    disabled={creatingFromTemplate === template.id}
                    onClick={() => handleUseTemplate(template)}
                    sx={{ textTransform: 'none' }}
                  >
                    {creatingFromTemplate === template.id ? 'Creating…' : 'Use template'}
                  </Button>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      ) : loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} variant='rounded' height={52} />
          ))}
        </Box>
      ) : error ? (
        <Typography color='error'>{error}</Typography>
      ) : dashboards.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <DashboardCustomizeRounded sx={{ fontSize: 40, color: 'text.disabled' }} />
          <Typography variant='h6' sx={{ mt: 1 }}>
            No dashboards yet
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Create your first custom dashboard to get started.
          </Typography>
        </Box>
      ) : (
        <Table size='small' sx={{ '& td, & th': { borderColor: 'divider' } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', width: 160 }}>
                Last modified
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', width: 140 }}>
                Client
              </TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {dashboards.map(dashboard => (
              <TableRow key={dashboard.id} hover sx={{ cursor: 'pointer' }} onClick={() => openDashboard(dashboard.id)}>
                <TableCell>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    {dashboard.name}
                  </Typography>
                  {dashboard.description && (
                    <Typography variant='caption' color='text.secondary'>
                      {dashboard.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant='body2' color='text.secondary'>
                    {new Date(dashboard.updated_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='body2' color='text.secondary'>
                    All clients
                  </Typography>
                </TableCell>
                <TableCell onClick={e => e.stopPropagation()}>
                  <IconButton
                    size='small'
                    onClick={e => setMenuAnchor({ el: e.currentTarget, id: dashboard.id })}
                    aria-label='More options'
                  >
                    <MoreVertRounded fontSize='small' />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Menu anchorEl={menuAnchor?.el} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          sx={{ fontSize: '0.875rem' }}
          onClick={() => {
            if (!menuAnchor) return;
            const row = dashboards.find(d => d.id === menuAnchor.id);
            setRenaming({ id: menuAnchor.id, name: row?.name ?? '' });
            setActionError(null);
            setMenuAnchor(null);
          }}
        >
          <DriveFileRenameOutline fontSize='small' sx={{ mr: 1 }} />
          Rename
        </MenuItem>
        <MenuItem
          sx={{ fontSize: '0.875rem' }}
          disabled={duplicatingId !== null}
          onClick={async () => {
            if (!menuAnchor) return;
            const id = menuAnchor.id;
            setMenuAnchor(null);
            setDuplicatingId(id);
            try {
              // Copy the widgets AND the dashboard config (filters / saved views /
              // branding) so the duplicate is a true copy, not just its layout.
              const source = await getDashboard(id);
              // flank#5101: defensive, same as handleUseTemplate — a dashboard
              // saved before a permission was revoked shouldn't carry a denied
              // field into its copy either, including a saved custom
              // dimension bucketed on `dsp` (CodeRabbit, same precedent as
              // DashboardBuilder.tsx's load-effect dspDimensionKeys).
              await awaitPlatformSignal(isRibeyeAdmin);
              const dspDimensionKeys = new Set(
                readTenantFields(source.config ?? null)
                  .dimensions.filter(d => d.field === 'dsp')
                  .map(d => d.key),
              );
              const { layout } = stripDeniedWidgetFields(
                source.layout ?? [],
                getCurrentPermissions(isRibeyeAdmin),
                dspDimensionKeys,
              );
              await createDashboard(`${source.name} (copy)`, source.description, layout, source.config ?? null);
              refresh();
            } finally {
              setDuplicatingId(null);
            }
          }}
        >
          <ContentCopyOutlined fontSize='small' sx={{ mr: 1 }} />
          {duplicatingId === menuAnchor?.id ? 'Duplicating…' : 'Duplicate'}
        </MenuItem>
        <MenuItem
          sx={{ color: 'error.main', fontSize: '0.875rem' }}
          // Confirm first: this was a single click that permanently destroyed a
          // dashboard with no confirmation and no undo.
          onClick={() => {
            if (!menuAnchor) return;
            const target = dashboards.find(d => d.id === menuAnchor.id);
            setConfirmDelete({ id: menuAnchor.id, name: target?.name ?? 'this dashboard' });
            setMenuAnchor(null);
          }}
        >
          <DeleteOutline fontSize='small' sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)} fullWidth maxWidth='xs'>
        <DialogTitle sx={{ pb: 0.5 }}>Delete dashboard?</DialogTitle>
        <Box sx={{ p: 3, pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant='body2' color='text.secondary'>
            {`"${confirmDelete?.name}" and all of its widgets will be permanently deleted. This can't be undone.`}
          </Typography>
          {actionError && (
            <Typography variant='body2' color='error'>
              {actionError}
            </Typography>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => setConfirmDelete(null)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant='contained'
              color='error'
              sx={{ textTransform: 'none' }}
              onClick={async () => {
                if (!confirmDelete) return;
                setActionError(null);
                try {
                  await deleteDashboard(confirmDelete.id);
                  setConfirmDelete(null);
                  refresh();
                } catch {
                  setActionError("Couldn't delete that dashboard. Please try again.");
                }
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Dialog>

      <RenameDashboardDialog
        open={Boolean(renaming)}
        initialName={renaming?.name ?? ''}
        saving={savingRename}
        error={actionError}
        onCancel={() => setRenaming(null)}
        onSave={async trimmedName => {
          if (!renaming) return;
          setSavingRename(true);
          setActionError(null);
          try {
            await updateDashboard(renaming.id, { name: trimmedName });
            setRenaming(null);
            refresh();
          } catch {
            setActionError("Couldn't rename that dashboard. Please try again.");
          } finally {
            setSavingRename(false);
          }
        }}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle sx={{ pb: 0.5 }}>New dashboard</DialogTitle>
        <Box sx={{ px: 3, pb: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            Give it a name; you can add widgets next.
          </Typography>
        </Box>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
          <TextField
            autoFocus
            required
            fullWidth
            label='Name'
            placeholder='Q4 Performance Overview'
            value={newName}
            onChange={event => setNewName(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') handleCreate();
            }}
          />
          {/*
            No Description field: `custom_dashboards` has no description column,
            the validator doesn't whitelist it and store() never assigns it, so
            AdonisJS dropped the key and anything typed here vanished on save.
            Collecting text we silently discard is worse than not asking for it.
            Restore this together with the backend column.
          */}
          <Box>
            <Typography variant='body2' sx={{ mb: 0.5, fontWeight: 500 }}>
              Client
            </Typography>
            <Select
              fullWidth
              size='small'
              disabled
              value={clientScope}
              onChange={event => setClientScope(event.target.value)}
            >
              <MenuItem value='all'>All clients</MenuItem>
            </Select>
            {/* Disabled rather than a one-option select that implies a choice:
                client scoping is blocked on account-level tenancy (#3699), and
                clientScope is not read by handleCreate. */}
            <FormHelperText>Client scoping arrives with account-level tenancy.</FormHelperText>
          </Box>
          {actionError && (
            <Typography variant='body2' color='error'>
              {actionError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant='contained'
            disableElevation
            disabled={creating || newName.trim().length === 0}
            onClick={handleCreate}
            sx={{ textTransform: 'none' }}
          >
            Start blank
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomDashboards;
